import React from 'react';
import { DownloadIcon } from '../../utils/icons';
import { Events, logEvent } from '../../utils/logging/events';

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
}

// Chrome and Edge fire this once, often before React mounts, so capture it at load.
let deferred: InstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  deferred = e as InstallPromptEvent;
  notify();
});
window.addEventListener('appinstalled', () => {
  deferred = null;
  notify();
});

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

// iOS has no install prompt: people have to use Safari's Share menu.
const isIosSafari = () => {
  const ua = navigator.userAgent;
  const ios =
    /iPhone|iPad|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  return ios && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
};

/** Offers "Install app" where the browser supports it, and renders nothing elsewhere. */
export const InstallButton: React.FC<{
  className?: string;
  wrapperClassName?: string;
}> = ({ className = '', wrapperClassName = '' }) => {
  const [, rerender] = React.useReducer((n: number) => n + 1, 0);
  const [showHelp, setShowHelp] = React.useState(false);

  React.useEffect(() => {
    listeners.add(rerender);
    return () => {
      listeners.delete(rerender);
    };
  }, []);

  if (isStandalone()) return null;
  const ios = isIosSafari();
  if (!deferred && !ios) return null;

  const install = async () => {
    logEvent(Events.INSTALL_APP);
    if (deferred) {
      await deferred.prompt();
      deferred = null;
      notify();
    } else {
      setShowHelp((s) => !s);
    }
  };

  return (
    <span className={`inline-flex flex-col gap-2 ${wrapperClassName}`}>
      <button
        type="button"
        onClick={install}
        aria-expanded={ios ? showHelp : undefined}
        className={`inline-flex items-center gap-2 ${className}`}
      >
        <DownloadIcon /> Install app
      </button>
      {ios && showHelp && (
        <span className="text-caption text-muted">
          Tap the Share button in Safari, then "Add to Home Screen".
        </span>
      )}
    </span>
  );
};
